import { isApiError } from "~/utils/apiClient";
import { StatusMessage } from "./StatusMessage";

export interface ResourceLoadErrorProps {
  // The SWR/apiClient error (an `ApiError` carries `.status`). May be absent
  // when a fetch resolved without data, which we treat as a generic failure.
  error: unknown;
  // Singular, lowercase noun for the resource, e.g. "project", "agent".
  resourceLabel: string;
  className?: string;
}

/**
 * Maps a load failure to status-aware copy. Distinguishes 404 (doesn't exist)
 * and 403 (no access) from generic failures, so a stale link or an
 * un-shared resource reads clearly instead of a blanket "failed to load".
 */
export const ResourceLoadError = ({ error, resourceLabel, className }: ResourceLoadErrorProps) => {
  const status = isApiError(error) ? error.status : undefined;

  if (status === 404) {
    return (
      <StatusMessage
        title={`This ${resourceLabel} doesn't exist`}
        body="It may have been deleted, or the link is out of date."
        className={className}
      />
    );
  }

  if (status === 403) {
    return (
      <StatusMessage
        title={`You don't have access to this ${resourceLabel}`}
        body="Ask an owner to share it with you."
        className={className}
      />
    );
  }

  return (
    <StatusMessage
      title={`Couldn't load ${resourceLabel}`}
      body="Something went wrong. Try again in a moment."
      className={className}
    />
  );
};
